/**
 * Mediasoup SFU Service
 * Connects to mediasoup server for media routing.
 * Enables server-side recording (media flows through server).
 */

import * as mediasoupClient from 'mediasoup-client';
import type { DtlsParameters, RtpCapabilities, RtpParameters } from 'mediasoup-client/lib/types';

export type MediasoupTransportDirection = 'send' | 'recv';

export interface MediasoupTransportOptions {
  id: string;
  iceParameters: any;
  iceCandidates: any[];
  dtlsParameters: DtlsParameters;
}

export type OnRemoteStream = (stream: MediaStream, kind: 'audio' | 'video') => void;

export class MediasoupService {
  private device: mediasoupClient.types.Device | null = null;
  private sendTransport: mediasoupClient.types.Transport | null = null;
  private recvTransport: mediasoupClient.types.Transport | null = null;
  private producers = new Map<string, mediasoupClient.types.Producer>();
  private consumers = new Map<string, mediasoupClient.types.Consumer>();
  private roomId: string | null = null;
  private socket: any = null;

  private onRemoteStreamCb: OnRemoteStream | null = null;

  /** Cached remote stream - same reference as WebRTCService for sync monitor compatibility */
  private remoteStream: MediaStream | null = null;
  private lastConsumerTrackIds = '';

  setSocket(socket: any) {
    this.socket = socket;
  }

  setRoomId(roomId: string) {
    this.roomId = roomId;
  }

  onRemoteStream(cb: OnRemoteStream) {
    this.onRemoteStreamCb = cb;
  }

  private async request<T>(event: string, data: any): Promise<T> {
    if (!this.socket || !this.roomId) throw new Error('Socket or roomId not set');
    return new Promise((resolve, reject) => {
      this.socket.emit(event, { ...data, roomId: this.roomId }, (response: any) => {
        if (response?.error) {
          reject(new Error(response.error));
        } else {
          resolve(response as T);
        }
      });
    });
  }

  async loadDevice(rtpCapabilities: RtpCapabilities): Promise<void> {
    this.device = new mediasoupClient.Device();
    await this.device.load({ routerRtpCapabilities: rtpCapabilities });
    console.log('[MediasoupService] Device loaded');
  }

  async createSendTransport(): Promise<mediasoupClient.types.Transport> {
    if (!this.device || !this.roomId || !this.socket) throw new Error('Not initialized');
    const data = await this.request<any>('mediasoup-create-transport', { direction: 'send' });
    if (data.error) throw new Error(data.error);
    this.sendTransport = this.device.createSendTransport({
      id: data.id,
      iceParameters: data.iceParameters,
      iceCandidates: data.iceCandidates,
      dtlsParameters: data.dtlsParameters,
    });
    this.sendTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
      try {
        await this.request('mediasoup-connect-transport', {
          transportId: this.sendTransport!.id,
          dtlsParameters,
          direction: 'send',
        });
        callback();
      } catch (e) {
        errback(e as Error);
      }
    });
    this.sendTransport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
      try {
        const data = await this.request<any>('mediasoup-produce', {
          transportId: this.sendTransport!.id,
          kind,
          rtpParameters,
          appData: { ...(appData || {}), socketId: this.socket?.id },
        });
        if (data.error) throw new Error(data.error);
        callback({ id: data.id });
      } catch (e) {
        errback(e as Error);
      }
    });
    this.sendTransport.on('connectionstatechange', (state) => {
      console.log('[MediasoupService] Send transport state:', state);
    });
    return this.sendTransport;
  }

  async createRecvTransport(): Promise<mediasoupClient.types.Transport> {
    if (!this.device || !this.roomId || !this.socket) throw new Error('Not initialized');
    const data = await this.request<any>('mediasoup-create-transport', { direction: 'recv' });
    if (data.error) throw new Error(data.error);
    this.recvTransport = this.device.createRecvTransport({
      id: data.id,
      iceParameters: data.iceParameters,
      iceCandidates: data.iceCandidates,
      dtlsParameters: data.dtlsParameters,
    });
    this.recvTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
      try {
        await this.request('mediasoup-connect-transport', {
          transportId: this.recvTransport!.id,
          dtlsParameters,
          direction: 'recv',
        });
        callback();
      } catch (e) {
        errback(e as Error);
      }
    });
    this.recvTransport.on('connectionstatechange', (state) => {
      console.log('[MediasoupService] Recv transport state:', state);
    });
    return this.recvTransport;
  }

  async produceTrack(track: MediaStreamTrack): Promise<string> {
    if (!this.sendTransport) throw new Error('Send transport not created');
    try {
      const s = track.getSettings?.();
      const c = track.getConstraints?.();
      console.log(
        `[MediasoupService] produceTrack() kind=${track.kind} id=${track.id} readyState=${track.readyState}`
      );
      console.log('[MediasoupService] local track settings=', s);
      console.log('[MediasoupService] local track constraints=', c);
    } catch (e) {
      console.log('[MediasoupService] track settings log failed', e);
    }
    const producer = await this.sendTransport.produce({
      track,
      appData: { socketId: this.socket?.id },
    });
    this.producers.set(producer.id, producer);
    return producer.id;
  }

  async consume(producerId: string): Promise<{ track: MediaStreamTrack; consumerId: string }> {
    if (!this.recvTransport || !this.device) throw new Error('Recv transport not created');
    const rtpCapabilities = this.device.rtpCapabilities;
    const data = await this.request<any>('mediasoup-consume', {
      producerId,
      rtpCapabilities,
    });
    if (data.error) throw new Error(data.error);
    // Replace stale consumers of same kind to avoid black remote video after producer refresh/rejoin.
    for (const [id, existing] of this.consumers.entries()) {
      const isSameKind = existing.kind === data.kind;
      const isClosedOrEnded = existing.closed || existing.track?.readyState === 'ended';
      if (isSameKind || isClosedOrEnded) {
        try { existing.close(); } catch {}
        this.consumers.delete(id);
      }
    }
    const consumer = await this.recvTransport.consume({
      id: data.id,
      producerId: data.producerId,
      kind: data.kind,
      rtpParameters: data.rtpParameters,
    });
    consumer.on('producerclose', () => {
      this.consumers.delete(consumer.id);
    });
    consumer.on('transportclose', () => {
      this.consumers.delete(consumer.id);
    });
    this.consumers.set(consumer.id, consumer);
    return { track: consumer.track, consumerId: consumer.id };
  }

  async getProducers(): Promise<Array<{ producerId: string; socketId: string; kind: string }>> {
    const data = await this.request<any>('mediasoup-get-producers', {});
    return data.producers || [];
  }

  getRemoteStream(): MediaStream | null {
    for (const [id, c] of this.consumers.entries()) {
      if (c.closed || c.track?.readyState === 'ended') {
        this.consumers.delete(id);
      }
    }
    const tracks = Array.from(this.consumers.values()).map((c) => c.track);
    if (tracks.length === 0) {
      this.remoteStream = null;
      this.lastConsumerTrackIds = '';
      return null;
    }
    const trackIds = tracks
      .map((t) => t.id)
      .sort()
      .join(',');
    if (this.remoteStream && this.lastConsumerTrackIds === trackIds) {
      return this.remoteStream;
    }
    // Reuse existing MediaStream to keep the same object reference.
    // This prevents the video element from needing a full reset when
    // new tracks arrive (e.g., audio arriving after video).
    if (!this.remoteStream) {
      this.remoteStream = new MediaStream();
    }
    // Remove tracks no longer present
    for (const existing of this.remoteStream.getTracks()) {
      if (!tracks.find((t) => t.id === existing.id)) {
        this.remoteStream.removeTrack(existing);
      }
    }
    // Add new tracks
    const currentIds = new Set(this.remoteStream.getTracks().map((t) => t.id));
    for (const t of tracks) {
      if (!currentIds.has(t.id)) {
        this.remoteStream.addTrack(t);
      }
    }
    this.lastConsumerTrackIds = trackIds;
    return this.remoteStream;
  }

  setMuted(muted: boolean): void {
    this.producers.forEach((p) => {
      if (p.kind === 'audio') {
        p.track.enabled = !muted;
      }
    });
  }

  setVideoEnabled(enabled: boolean): void {
    this.producers.forEach((p) => {
      if (p.kind === 'video') {
        p.track.enabled = enabled;
      }
    });
  }

  /**
   * Replace the video track (for camera flip/switch).
   * Keeps the same producer so remote consumers and recording continue seamlessly.
   */
  async replaceVideoTrack(newTrack: MediaStreamTrack): Promise<void> {
    for (const producer of this.producers.values()) {
      if (producer.kind === 'video') {
        // Instrumentation: log new track and producer before replace
        try {
          const s = newTrack.getSettings?.();
          const c = newTrack.getConstraints?.();
          const caps = typeof newTrack.getCapabilities === 'function' ? newTrack.getCapabilities() : {};
          console.log('[MediasoupService] [CameraFlip] BEFORE replaceTrack — producerId:', producer.id, 'newTrack:', {
            id: newTrack.id,
            settings: s ? { width: s.width, height: s.height, frameRate: s.frameRate, facingMode: s.facingMode } : undefined,
            constraints: c,
            capabilities: caps ? { width: (caps as any).width, height: (caps as any).height } : undefined,
          });
        } catch (e) {
          console.warn('[MediasoupService] Could not log track:', e);
        }
        await producer.replaceTrack({ track: newTrack });
        console.log('[MediasoupService] Replaced video track on producer', producer.id);
        // Log producer state after replace (mediasoup-client Producer may expose rtpParameters)
        try {
          const rtpParams = (producer as any).rtpParameters;
          if (rtpParams?.encodings?.length) {
            console.log('[MediasoupService] [CameraFlip] AFTER replaceTrack — producer encodings:', rtpParams.encodings.map((e: any) => ({
              maxBitrate: e.maxBitrate,
              scaleResolutionDownBy: e.scaleResolutionDownBy,
              rid: e.rid,
            })));
          }
        } catch (_) {}
        // Notify server to request keyframes from all consumers (peer + recording consumer)
        // and reset the recording relay's RTP continuity state.
        // Without this, the remote side and recording freeze until a keyframe naturally arrives.
        if (this.socket && this.roomId) {
          this.socket.emit('mediasoup-replace-track', { roomId: this.roomId, kind: 'video' });
        }
        return;
      }
    }
    throw new Error('No video producer found to replace');
  }

  toggleMute(): boolean {
    for (const p of this.producers.values()) {
      if (p.kind === 'audio') {
        p.track.enabled = !p.track.enabled;
        return !p.track.enabled; // return isMuted
      }
    }
    return false;
  }

  toggleVideo(): boolean {
    for (const p of this.producers.values()) {
      if (p.kind === 'video') {
        p.track.enabled = !p.track.enabled;
        return p.track.enabled; // return isVideoEnabled
      }
    }
    return false;
  }

  stopLocalStream(): void {
    this.producers.forEach((p) => p.track.stop());
  }

  closePeerConnection(): void {
    this.close();
  }

  cleanup(): void {
    this.close();
  }

  close(): void {
    this.sendTransport?.close();
    this.recvTransport?.close();
    this.producers.forEach((p) => p.close());
    this.consumers.forEach((c) => c.close());
    this.sendTransport = null;
    this.recvTransport = null;
    this.producers.clear();
    this.consumers.clear();
    this.remoteStream = null;
    this.lastConsumerTrackIds = '';
    this.device = null;
    this.roomId = null;
    console.log('[MediasoupService] Closed');
  }
}
